import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Check } from "lucide-react";

const CATEGORY_HINT = "technology,business,science,health,politics,world,entertainment,sports,culture,gaming,finance,lifestyle".split(",");

const CREATIVE_CATEGORIES = [
  "Podcaster",
  "YouTuber",
  "TikTok creator",
  "Instagram creator",
  "Streamer (Twitch)",
  "Newsletter writer",
  "Substack author",
  "Blogger",
  "Journalist",
  "Essayist",
  "Ghostwriter",
  "Comedian",
  "Filmmaker",
  "Documentarian",
  "Radio host",
  "Musician",
  "Poet",
  "Author (books)",
  "Speaker / keynote",
  "Educator / course creator",
  "Coach / consultant",
  "Community host",
  "Marketer / brand",
  "Analyst",
];
const MAX_CREATIVE_CATEGORIES = 3;

const STORYTELLING = [
  "Narrative",
  "Interview",
  "Debate",
  "Explainer",
  "Comedic",
  "Investigative",
  "Conversational",
  "Monologue",
  "Documentary",
  "Reactive / hot take",
];
const MAX_STORYTELLING = 2;

const GENRES = [
  "News commentary",
  "Educational",
  "Entertainment",
  "True crime",
  "Sports",
  "Pop culture",
  "Tech analysis",
  "Business strategy",
  "Self-improvement",
  "Politics & policy",
  "Culture & arts",
  "Comedy",
];
const MAX_GENRES = 2;

// Map stored values to canonical option labels (case-insensitive) and drop
// unknowns. Legacy singular fields were stored lowercase / as free text (e.g.
// "informative"); unmapped values would otherwise invisibly consume cap slots
// and permanently lock the group.
function canonicalize(values, options, fallback) {
  const mapped = (values || [])
    .map((v) => options.find((o) => o.toLowerCase() === String(v).trim().toLowerCase()))
    .filter(Boolean);
  const unique = [...new Set(mapped)];
  return unique.length ? unique : [fallback];
}

export default function Onboarding() {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    niche: "",
    genres: [GENRES[0]],
    storytelling_methods: [STORYTELLING[0]],
    creative_categories: [CREATIVE_CATEGORIES[0]],
    voice_notes: "",
    categories_of_interest: [],
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.categories || CATEGORY_HINT));
    if (user?.profile) {
      const p = user.profile;
      const creative = canonicalize(
        (Array.isArray(p.creative_categories) && p.creative_categories.length
          ? p.creative_categories
          : [p.creative_category]),
        CREATIVE_CATEGORIES,
        CREATIVE_CATEGORIES[0]
      );
      const genresSel = canonicalize(
        (Array.isArray(p.genres) && p.genres.length ? p.genres : [p.genre]),
        GENRES,
        GENRES[0]
      );
      const methodsSel = canonicalize(
        (Array.isArray(p.storytelling_methods) && p.storytelling_methods.length
          ? p.storytelling_methods
          : [p.storytelling_method]),
        STORYTELLING,
        STORYTELLING[0]
      );
      setForm({
        niche: p.niche || "",
        genres: genresSel.slice(0, MAX_GENRES),
        storytelling_methods: methodsSel.slice(0, MAX_STORYTELLING),
        creative_categories: creative.slice(0, MAX_CREATIVE_CATEGORIES),
        voice_notes: p.voice_notes || "",
        categories_of_interest: p.categories_of_interest || [],
      });
    }
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const toggleCat = (c) => {
    setForm((f) => ({
      ...f,
      categories_of_interest: f.categories_of_interest.includes(c)
        ? f.categories_of_interest.filter((x) => x !== c)
        : [...f.categories_of_interest, c],
    }));
  };

  // Generic toggle for capped multi-select fields. Always keeps at least one selected.
  const toggleCapped = (field, value, max) => {
    setForm((f) => {
      const current = f[field] || [];
      const has = current.includes(value);
      if (has) {
        if (current.length <= 1) return f; // must keep at least one
        return { ...f, [field]: current.filter((x) => x !== value) };
      }
      if (current.length >= max) return f;
      return { ...f, [field]: [...current, value] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser({ ...user, profile: data });
      nav("/app");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
        <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gray-500">
          Step 01 · Profile
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none mt-2">
          Set your voice.
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mt-3 max-w-xl leading-relaxed">
          Tell us how you create. We&apos;ll shape every talking point, hook, and title to your voice.
        </p>

        <form onSubmit={submit} className="mt-8 sm:mt-10 space-y-7 sm:space-y-8" data-testid="onboarding-form">
          <Group label="Niche">
            <input
              data-testid="profile-niche-input"
              value={form.niche}
              onChange={(e) => setForm({ ...form, niche: e.target.value })}
              required
              placeholder="e.g. AI ethics for product managers"
              className="w-full border border-gray-300 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none px-4 py-3 text-[15px] rounded-lg"
            />
          </Group>

          <MultiSelectGroup
            label="Creative category"
            options={CREATIVE_CATEGORIES}
            selected={form.creative_categories}
            max={MAX_CREATIVE_CATEGORIES}
            hint="Pick up to 3. If you wear more than one hat, we'll blend the voice."
            onToggle={(v) => toggleCapped("creative_categories", v, MAX_CREATIVE_CATEGORIES)}
            testid="profile-creative-category"
          />
          <MultiSelectGroup
            label="Genre"
            options={GENRES}
            selected={form.genres}
            max={MAX_GENRES}
            hint="Pick up to 2 genres so we can straddle both when the story fits."
            onToggle={(v) => toggleCapped("genres", v, MAX_GENRES)}
            testid="profile-genre"
          />
          <MultiSelectGroup
            label="Storytelling method"
            options={STORYTELLING}
            selected={form.storytelling_methods}
            max={MAX_STORYTELLING}
            hint="Pick up to 2. Perfect for creators who narrate one week and debate the next."
            onToggle={(v) => toggleCapped("storytelling_methods", v, MAX_STORYTELLING)}
            testid="profile-storytelling"
          />

          <Group label="Voice notes (optional)">
            <textarea
              data-testid="profile-voice-notes"
              value={form.voice_notes}
              onChange={(e) => setForm({ ...form, voice_notes: e.target.value })}
              rows={3}
              placeholder="Dry wit, contrarian, refuses corporate jargon..."
              className="w-full border border-gray-300 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none px-4 py-3 text-[15px] rounded-lg"
            />
          </Group>

          <Group label="Categories of interest">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => {
                const active = form.categories_of_interest.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    data-testid={`profile-category-${c}`}
                    onClick={() => toggleCat(c)}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-[11px] sm:text-xs font-mono uppercase tracking-widest border rounded-md transition-colors ${
                      active
                        ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                        : "bg-white text-gray-700 border-gray-300 hover:border-[#0a0a0a]"
                    }`}
                  >
                    {active && <Check size={12} strokeWidth={2} />}
                    {c}
                  </button>
                );
              })}
            </div>
          </Group>

          <div className="pt-4">
            <button
              data-testid="profile-submit-button"
              disabled={busy}
              className="w-full sm:w-auto bg-brand-gradient text-white hover:opacity-95 disabled:opacity-60 px-8 py-3.5 sm:py-3 font-semibold inline-flex items-center justify-center gap-2 rounded-lg shadow-[0_18px_50px_-15px_rgba(124,58,237,0.55)]"
            >
              {busy && <Loader2 size={16} className="animate-spin" />}
              {user?.profile ? "Save profile" : "Enter Fount"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-gray-500 mb-2">
        {label}
      </div>
      {children}
    </div>
  );
}

function MultiSelectGroup({ label, options, selected, max, hint, onToggle, testid }) {
  const atCap = selected.length >= max;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-gray-500">
          {label}
        </div>
        <div
          data-testid={`${testid}-count`}
          className={`font-mono text-[10px] uppercase tracking-widest ${
            atCap ? "text-[#7C3AED]" : "text-gray-400"
          }`}
        >
          {selected.length}/{max}
        </div>
      </div>
      {hint && (
        <div className="text-[11px] text-gray-500 mb-2 leading-relaxed">{hint}</div>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = selected.includes(o);
          const disabled = !active && atCap;
          return (
            <button
              key={o}
              type="button"
              data-testid={`${testid}-${o}`}
              onClick={() => onToggle(o)}
              disabled={disabled}
              className={`inline-flex items-center gap-1.5 px-3.5 sm:px-4 py-2 text-[13px] sm:text-sm border rounded-md transition-colors ${
                active
                  ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                  : disabled
                    ? "bg-white text-gray-300 border-gray-200 cursor-not-allowed"
                    : "bg-white text-gray-700 border-gray-300 hover:border-[#0a0a0a]"
              }`}
            >
              {active && <Check size={12} strokeWidth={2} />}
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
